import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Receipt, Wifi, WifiOff, RefreshCw, Power, PowerOff, ChevronRight,
  AlertTriangle, Database, Loader2, X,
} from "lucide-react";
import ConnectorCard from "@/components/ConnectorCard";

// Phase 25.1 — MYOB OAuth panel.
// Phase 25.7  — Switched to ConnectorCard pattern (mobile-friendly).

function fmtTime(iso) {
  if (!iso) return "—";
  const dt = new Date(iso);
  const diffMs = Date.now() - dt.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  if (mins < 1440) return `${Math.floor(mins / 60)} h ago`;
  return dt.toLocaleString();
}

export default function MyobConnectionPanel() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pickerFiles, setPickerFiles] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/myob/status");
      setStatus(r.data);
      if (r.data?.connected && r.data?.status === "pending_cf") {
        await openPicker();
      }
    } catch {
      setStatus({ connected: false, _err: true });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("myob") === "connected_pick_cf") {
      toast.success("MYOB connected. Pick a company file to start syncing.", { duration: 6000 });
      setTimeout(() => openPicker(), 500);
      params.delete("myob");
      const next = params.toString();
      window.history.replaceState({}, "", window.location.pathname + (next ? `?${next}` : ""));
    }
    const err = params.get("myob_error");
    if (err) {
      toast.error(`MYOB connection failed: ${err}`);
      params.delete("myob_error");
      const next = params.toString();
      window.history.replaceState({}, "", window.location.pathname + (next ? `?${next}` : ""));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connect = async () => {
    setConnecting(true);
    try {
      const r = await api.get("/myob/connect");
      if (r.data?.authorize_url) {
        window.location.href = r.data.authorize_url;
      } else {
        toast.error("No authorize URL returned");
        setConnecting(false);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || "Connect failed";
      toast.error(detail);
      if (detail.includes("not configured")) {
        toast.info("Paste your MYOB keys under Settings → Platform Keys first.", { duration: 8000 });
      }
      setConnecting(false);
    }
  };

  const openPicker = async () => {
    setPickerFiles([]);
    try {
      const r = await api.get("/myob/company-files");
      setPickerFiles(r.data?.files || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to fetch MYOB company files");
      setPickerFiles(null);
    }
  };

  const pickFile = async (file) => {
    setBusy(true);
    try {
      await api.post("/myob/select-cf", {
        cf_id: file.Id,
        cf_name: file.Name || "",
        cf_country: file.Country || "",
      });
      toast.success(`Connected to "${file.Name}". First sync running…`, { duration: 6000 });
      setPickerFiles(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to select company file");
    } finally {
      setBusy(false);
    }
  };

  const sync = async () => {
    setBusy(true);
    try {
      const r = await api.post("/myob/sync");
      if (r.data?.ok) {
        const s = r.data.stats || {};
        toast.success(`Synced — ${s.invoices || 0} invoices · ${s.customers || 0} customers · ${s.accounts || 0} accounts`);
      } else {
        toast.error(`Sync failed: ${r.data?.reason || "unknown"}`);
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect MYOB? Cached invoices, customers, and accounts will be wiped.")) return;
    setBusy(true);
    try {
      await api.post("/myob/disconnect");
      toast.success("MYOB disconnected");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Disconnect failed");
    } finally {
      setBusy(false);
    }
  };

  if (loading) return null;
  if (status?._err) return null;

  const connected = status?.connected;
  const isActive = connected && status.status === "active";
  const isPendingCf = connected && status.status === "pending_cf";

  // Build the ConnectorCard props
  const cardStatus = !connected
    ? "none"
    : isActive
      ? "connected"
      : isPendingCf
        ? "warning"
        : "disconnected";

  const statusLabel = !connected
    ? "NOT CONNECTED"
    : isActive
      ? "● LIVE"
      : isPendingCf
        ? "PICK CF"
        : "DISCONNECTED";

  return (
    <>
      <ConnectorCard
        title="// MYOB · ACCOUNTRIGHT LIVE (AU/NZ)"
        testId="myob-connection-panel"
        icon={connected ? (isActive ? Wifi : isPendingCf ? Database : WifiOff) : Receipt}
        name={connected ? (status.cf_name || "MYOB · Pending company-file pick") : "Connect MYOB"}
        tagline={
          connected
            ? `Connected ${fmtTime(status.connected_at)}${status.last_sync_at ? ` · Last sync ${fmtTime(status.last_sync_at)}` : ""}`
            : "Read-only access to AccountRight Live invoices, customers, and accounts. OAuth takes 60 seconds. Built for the 1.2M MYOB merchants in AU/NZ."
        }
        status={cardStatus}
        statusLabel={statusLabel}
        badges={status?.cf_country ? (
          <span className="px-2 py-0.5 border border-zinc-800 bg-zinc-950 text-zinc-400 font-mono text-[9px] uppercase tracking-widest">
            {status.cf_country}
          </span>
        ) : null}
        primaryAction={
          !connected ? (
            <button
              onClick={connect}
              disabled={connecting}
              data-testid="myob-connect-btn"
              className="w-full sm:w-auto bg-[#FFB000] text-black font-bold font-mono text-[11px] uppercase tracking-widest px-5 py-3 hover:opacity-90 disabled:opacity-50 inline-flex items-center justify-center gap-2"
            >
              <Power className="w-3.5 h-3.5" />
              {connecting ? "OPENING…" : "CONNECT MYOB"}
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          ) : isPendingCf ? (
            <button
              onClick={openPicker}
              disabled={busy}
              data-testid="myob-pick-cf-btn"
              className="w-full sm:w-auto bg-[#FFB000] text-black font-bold font-mono text-[11px] uppercase tracking-widest px-5 py-3 hover:opacity-90 disabled:opacity-50 inline-flex items-center justify-center gap-2"
            >
              <Database className="w-3.5 h-3.5" />
              PICK COMPANY FILE
            </button>
          ) : isActive ? (
            <button
              onClick={sync}
              disabled={busy}
              data-testid="myob-sync-btn"
              className="w-full sm:w-auto bg-[#FFB000] text-black font-bold font-mono text-[11px] uppercase tracking-widest px-5 py-3 hover:opacity-90 disabled:opacity-50 inline-flex items-center justify-center gap-2"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} />
              SYNC NOW
            </button>
          ) : null
        }
        secondaryActions={connected ? (
          <button
            onClick={disconnect}
            disabled={busy}
            data-testid="myob-disconnect-btn"
            className="w-full sm:w-auto border border-[#FF6B47]/40 text-[#FF6B47] hover:bg-[#FF6B47]/10 px-3 py-2.5 font-mono text-[11px] uppercase tracking-widest inline-flex items-center justify-center gap-2 disabled:opacity-50"
          >
            <PowerOff className="w-3.5 h-3.5" />
            DISCONNECT
          </button>
        ) : null}
        extra={connected && !isActive && !isPendingCf ? (
          <div className="flex items-start gap-2 border border-[#FF6B47]/30 bg-[#FF6B47]/5 p-3">
            <AlertTriangle className="w-4 h-4 text-[#FF6B47] flex-shrink-0 mt-0.5" />
            <div className="text-xs text-zinc-300">
              Connection lost. Tap <strong className="text-white">CONNECT MYOB</strong> to re-link this workspace.
            </div>
          </div>
        ) : null}
      />

      {/* Company file picker dialog */}
      {pickerFiles !== null && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
          data-testid="myob-cf-picker"
          onClick={() => !busy && setPickerFiles(null)}
        >
          <div
            className="w-full max-w-2xl bg-black border border-zinc-800 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-zinc-900 px-5 py-3">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">// PICK COMPANY FILE</div>
                <div className="font-heading font-black text-base tracking-tight">YOUR MYOB FILES</div>
              </div>
              <button
                onClick={() => !busy && setPickerFiles(null)}
                className="text-zinc-500 hover:text-white"
                data-testid="myob-cf-picker-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5 max-h-[60vh] overflow-y-auto">
              {pickerFiles.length === 0 && (
                <div className="text-center py-10">
                  <Loader2 className="w-5 h-5 animate-spin text-[#FFB000] mx-auto" />
                  <div className="font-mono text-[10px] text-zinc-500 mt-3">FETCHING FROM MYOB…</div>
                </div>
              )}
              {pickerFiles.length > 0 && (
                <div className="space-y-2">
                  {pickerFiles.map((f) => (
                    <button
                      key={f.Id}
                      onClick={() => pickFile(f)}
                      disabled={busy}
                      data-testid={`myob-cf-${f.Id}`}
                      className="w-full text-left border border-zinc-900 hover:border-[#FFB000]/50 hover:bg-zinc-950 p-4 transition-colors group disabled:opacity-50"
                    >
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="font-heading font-bold text-base text-white">{f.Name}</div>
                          <div className="font-mono text-[10px] text-zinc-500 mt-1 flex items-center gap-3 flex-wrap">
                            {f.Country && <span>{f.Country}</span>}
                            {f.ProductLevel && <span>· {f.ProductLevel}</span>}
                            {f.Id && <span className="text-zinc-700">· {f.Id.slice(0, 8)}…</span>}
                          </div>
                        </div>
                        <ChevronRight className="w-4 h-4 text-zinc-600 group-hover:text-[#FFB000]" />
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
